<template>
    <div class="modal-container modal-container-corner no-transition" :class="{ hidden: !display }">
        <div class="modal-dialog modal-md">
            <h1 class="text-lg font-semibold mb-4">{{ $t('Notifications') }}</h1>

            <div v-if="notifications.length > 0" class="notification-list" ref="scrollArea">
                <div
                    v-for="(event, index) in notifications"
                    :key="index"
                    class="notification-item"
                    :class="{ read: event.read }"
                >
                    <div class="notification-header">
                        <p @click="goToNotifications()">{{ event.title }}</p>
                        <div class="actions-group">
                            <button
                                v-if="event.description && !event.read"
                                @click="toggleDescription(index)"
                                type="button"
                                class="toggle-description-btn"
                                :aria-label="eventStates[index]?.showDescription ? `${$t('Hide details')}` : `${$t('Show details')}`"
                                :title="eventStates[index]?.showDescription ? `${$t('Hide details')}` : `${$t('Show details')}`"
                            >
                                <span v-if="!eventStates[index]?.showDescription" class="arrow-icon"><i class="fa-solid fa-arrow-down"></i></span>
                                <span v-else class="arrow-icon"><i class="fa-solid fa-arrow-up"></i></span>
                            </button>
                            <button
                                @click="markAsRead(index)"
                                type="button"
                                class="mark-read-btn icon-btn"
                                :aria-label="event.read ? `${$t('Mark as unread')}` : `${$t('Mark as read')}`"
                                :title="event.read ? `${$t('Mark as unread')}` : `${$t('Mark as read')}`"
                            >
                                <i :class="event.read ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                            </button>
                            <button
                                @click="archiveNotification(index)"
                                type="button"
                                class="delete-btn icon-btn"
                                :aria-label="$t('Archive notification')"
                                :title="$t('Archive notification')"
                            >
                                <i class="fa fa-folder-plus"></i>
                            </button>
                        </div>
                    </div>

                    <p v-if="eventStates[index]?.showDescription && event.description" class="notification-description">
                        {{ event.description }}
                    </p>

                    <p v-if="event.schedule && event.schedule.startDate" class="notification-datetime">
                        {{ $t('Notification date:') }} {{ formatDateTime(event.schedule.startDate) }}
                    </p>

                    <p v-else class="notification-datetime">
                        {{ $t('Notification date:') }} {{ formatDateTimeTimestamp(event.timestamp) }}
                    </p>

                    <p v-if="event.readTimestamp" class="notification-datetime">
                        <span>{{ $t('Read on:') }} </span> {{ formatDateTimeTimestamp(event.readTimestamp) }}
                    </p>
                </div>
            </div>

            <div v-else class="no-notifications">
                <p>{{ $t('No notifications available') }}</p>
            </div>

            <div class="footer" v-if="notifications.length > 0">
                <button class="see-notifications" @click="goToNotifications()">
                    {{ $t('See notifications') }}
                </button>
                <button v-if="hasUnread" class="mark-all-btn" @click="markAllAsRead">
                    {{ $t('Mark all as read') }}
                </button>
            </div>
        </div>
    </div>
</template>
  
<script>
import { FocusTrap } from "@/utils/focus-trap";
import { formatDateTime, formatDateTimeTimestamp } from "@/utils/utils";
import { ApiEvents } from "@/api/api-events";
import { Request } from "@asanrom/request-browser";

export default {
    name: "NotificationsDropdown",
    emits: ["update:display", "updateUnreadCount", "update:notifications", 'updateNotifications'],
    props: {
        display: Boolean,
        notifications: Array,
    },
    data() {
        return {
            focusTrap: null,
            eventStates: [],
            loading: false,
            error: null,
        };
    },
    computed: {
        hasUnread() {
            return this.notifications.some((n) => !n.read);
        },
        unreadCount() {
            return this.notifications.filter((n) => !n.read).length;
        }
    },
    methods: {
        markAsRead(index) {
            const event = this.notifications[index];
            if (event.read) {
                Request.Pending(
                    "mark-notification-unread",
                    ApiEvents.MarkEventAsUnread(event.id)
                )
                .onSuccess(() => {
                    this.notifications[index].read = false;
                    this.notifications[index].readTimestamp = "";
                    this.$emit("updateUnreadCount", this.unreadCount); 
                    this.$emit('updateNotifications', this.notifications); 
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$router.push("/login");
                        },
                        notFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        notFoundNotificationNotFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        temporalError: () => {
                            console.error(`${this.$t('Temporal error marking notification as unread')}`);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    console.error(`${this.$t('Error marking notification as unread')}`);
                });
            } else {
                Request.Pending(
                    "mark-notification-read",
                    ApiEvents.MarkEventAsRead(event.id)
                )
                .onSuccess(() => {
                    this.notifications[index].read = true;
                    this.notifications[index].readTimestamp = new Date().toISOString();
                    this.$emit("updateUnreadCount", this.unreadCount);
                    this.$emit('updateNotifications', this.notifications); 
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$router.push("/login");
                        },
                        notFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        notFoundNotificationNotFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        temporalError: () => {
                            console.error(`${this.$t('Temporal error marking notification as read')}`);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    console.error(`${this.$t('Error marking notification as read')}`);
                });
            }
        },
        markAllAsRead() {
            Request.Pending("mark-all-read", ApiEvents.MarkAllEventsAsRead())
            .onSuccess(() => {
                this.notifications.forEach((notification) => {
                    notification.read = true;
                });
                this.$emit("updateUnreadCount", this.unreadCount); 
            })
        },
        archiveNotification(index){
            const event = this.notifications[index];
            if (!event.archived) {
                Request.Pending(
                    "mark-notification-archived",
                    ApiEvents.ArchiveEvent(event.id)
                )
                .onSuccess(() => {
                    this.notifications[index].archived = true;
                    this.notifications[index].archivedTimestamp = new Date().toISOString();
                    this.notifications.splice(index, 1);
                    this.loadEvents();
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$router.push("/login");
                        },
                        notFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        notFoundNotificationNotFound: () => {
                            console.error(`${this.$t('Notification not found')}`);
                        },
                        temporalError: () => {
                            console.error(`${this.$t('Temporal error marking notification as archived')}`);
                        },
                    });
                })
                .onUnexpectedError(() => {
                    console.error(`${this.$t('Error marking notification as archived')}`);
                });
            } 
        },
        close() {
            this.$emit("update:display", false);
        },
        escapeToClose(event) {
            if (event.key === "Escape") {
                this.close();
            }
        },
        goToNotifications(){
            this.close();
            this.$router.push("/notifications");
        },
        stopPropagationEvent(e) {
            e.stopPropagation();
        },
        toggleDescription(index) {
            const newEventStates = [...this.eventStates];
            if (!newEventStates[index]) {
                newEventStates[index] = { showDescription: false };
            }

            newEventStates[index] = {
                ...newEventStates[index],
                showDescription: !newEventStates[index].showDescription,
            };

            this.eventStates = newEventStates;
        },
        loadEvents() {
            if (this.loading) return;
            
            this.loading = true;
            this.error = null;

            const lang = localStorage.getItem("app-pref-lang") || "en";
            
            Request.Pending(
                "load-notifications-dropdown",
                ApiEvents.GetUserEvents(0, 10, lang, 'notification')
            )
            .onSuccess((events) => {
                this.$emit('update:notifications', events || []);
                this.loading = false;
            })
            .onCancel(() => {
                this.loading = false;
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        this.$router.push("/login");
                    },
                    badRequest: () => {
                        console.error(`${this.$t('Bad request loading notifications')}`);
                    },
                    temporalError: () => {
                        this.error = this.$t('Error loading notifications');
                    },
                });
                this.loading = false;
            })
            .onUnexpectedError(() => {
                console.error(`${this.$t('Error loading notifications')}`);
                this.error = this.$t('Error loading notifications');
                this.loading = false;
            });
        },
        formatDateTime,
        formatDateTimeTimestamp
    },
    mounted() {
        this.focusTrap = new FocusTrap(this.$el, this.close.bind(this), "btn-top-bar-notifictions");
        if (this.display) {
            this.focusTrap.activate();
            this.$nextTick(() => {
                this.$el.focus();
            });
        }
        this.loadEvents();
    },
    watch: {
        display(newVal) {
            if (newVal) {
                this.loadEvents();
            }
        },
    },
    beforeUnmount() {
        if (this.focusTrap) {
            this.focusTrap.destroy();
        }
    },
};
</script>
<style scoped>
.modal-container.modal-container-corner .modal-dialog{
    width: 350px;
    border-radius: 0;
}
.modal-container.modal-container-corner{
    padding-right: 20px;
}

.no-notifications p{
    margin: 10px 0 10px 0;
}

.delete-btn{
    border: none;
    cursor: pointer;
    background-color: transparent;
}

.mark-all-btn:hover, .see-notifications:hover{
    background-color: var(--button-primary-bg-hover);
}
.dark-theme .notification-header p, .dark-theme .notification-datetime, .dark-theme .arrow-icon, .dark-theme .notification-description{
    color: #fff;
}
.modal-dialog {
  background: var(--modal-bg-color);
  padding: 1rem;
  border-radius: 0.5rem;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.text-lg {
  margin: 5px 0;
  font-size: 18px;
}
.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 0.5rem;
}

.notification-item {
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  flex-direction: column; 
  align-items: stretch;
}

.notification-item:last-child {
  border-bottom: none;
}
.notification-item.read p{
    color: #999;
}

.notification-item.read .notification-header > p {
  text-decoration: line-through;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center; 
  width: 100%;
}

.notification-header p {
  margin: 0; 
  flex-grow: 1;
  color: initial;
  cursor: pointer;
}

.actions-group {
  display: flex;
  align-items: center;
  gap: 5px;
}

.toggle-description-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.arrow-icon {
  display: inline-block;
  transition: transform 0.2s; 
  padding-right: 5px;
  padding-bottom: 1px;
}

.mark-read-btn {
  background-color: transparent;
  color: var(--primary-color);
  border: none;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  flex-shrink: 0;
}

.mark-read-btn:hover {
  text-decoration: underline;
}

.footer {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}

.mark-all-btn, .see-notifications {
  background-color: var(--button-primary-bg);
  color: white;
  border: none;
  padding: 10px;
  border-radius: 0.375rem;
  cursor: pointer;
}
.notification-description {
    margin-top: 0.5rem;
    font-size: 12px;
    color: #555;
    line-height: 1.4;
    width: 100%;
    box-sizing: border-box;
}
.notification-datetime {
    font-size: 11px;
    color: #3b3b3b;
    text-align: left;
    margin-top: 0;
    margin-bottom: 0;
    padding-right: 5px;
}
@media (max-width: 450px) {
    .footer{
        display: block;
    }
    .mark-all-btn, .see-notifications{
        width: 100%;
         margin: 5px 0;
         font-size: 12px;
    }
}
</style>
  
